// Shared SO freight math (ticket 86d39jbhk). Single source of truth so the Line Items
// footer block, the compact Shipping & Billing summary, and the Pricing panel's combined
// total can never disagree.
//
// Deliberately simpler than the quotation freight engine (FreightAllocator): the per-ton rate
// is already VAT-INCLUSIVE, so the SO does NOT recompute it — no destination/DopRate, no
// Metro-Manila flat mode, no truck min-billable floor, no numTrucks, no toll/padding/discount/
// round-up, and NO separate freight VAT.
//
//   rate   = freightPerTon   (the coordinator-editable Freight/Ton, defaults from FO; VAT-inclusive)
//   tons   = totalWeightKg / 1000
//   charge = waived ? 0 : tons × rate                (VAT-inclusive; the rate already is)
//
// 86d3fteqa: the coordinator-editable Freight/Ton rate DRIVES the charge (it defaults from the
// FO's custbody_quot_freightchargeperton but is overridable). ActualFreightPerMt is informational
// only and no longer participates. `isTrailer` is a delivery flag, not a cost input;
// `isDiscountedFreight` / `discountDetails` are informational only and do NOT change the number.

export interface SoFreightInput {
  /** Total delivered weight in kg, summed across the line items. */
  totalWeightKg: number;
  /** Effective freight rate per ton (VAT-inclusive): the coordinator-editable Freight/Ton,
   *  which defaults from the FO rate. Null when neither is set. (86d3fteqa) */
  freightPerTon: number | null;
  /** True when Waived Freight = Yes (draft.waivedFreight === '1'). */
  waived: boolean;
}

export interface SoFreight {
  /** Per-MT rate in effect (VAT-inclusive). Null when no rate is set. */
  rate: number | null;
  /** Delivered tonnage (metric tons). */
  tons: number;
  /** Computed freight charge (VAT-inclusive). 0 when waived; null when no rate is known. */
  charge: number | null;
  waived: boolean;
}

// Compute the SO freight charge from the model above.
export function computeSoFreight(input: SoFreightInput): SoFreight {
  const { totalWeightKg, freightPerTon, waived } = input;
  const rate = freightPerTon ?? null;
  const tons = totalWeightKg / 1000;
  const charge = waived ? 0 : rate != null ? tons * rate : null;
  return { rate, tons, charge, waived };
}

// Combined order total = rebar grand total (incl. rebar VAT) + freight charge (VAT-inclusive).
// Both gross → apples-to-apples, matching what NetSuite posts. Freight contributes 0 when
// waived and is skipped (rebar-only) when no freight rate is known yet.
export function combinedOrderTotal(rebarGrandTotal: number, freightCharge: number | null): number {
  return rebarGrandTotal + (freightCharge ?? 0);
}

// ─── Freight billing disposition (86d3addwy) ────────────────────────────────
// The Deliver/Bill-to company's custentity_cust_separatefreightyn drives the SO
// custbody_so_separatebilling value. The two NetSuite lists differ (same ids, different
// meaning), so the mapping is explicit — NOT an id passthrough.

/** SO custbody_so_separatebilling option ids. */
export const SeparateBilling = {
  Yes: '1', // freight billed separately (on another document)
  No: '2', // freight is a distinct line on THIS SO (default)
  FreightIncorporated: '3', // freight baked into the item price
} as const;

/** Map the customer's custentity_cust_separatefreightyn (1=N/A, 2=Incorporate on Price,
 *  3=Separate Billing) to the SO separate-billing id (Sales-ratified mapping). */
export function separateBillingFromCustomer(separateFreightYn: string | null | undefined): string {
  switch (separateFreightYn) {
    case '3':
      return SeparateBilling.Yes; // Separate Billing → YES
    case '2':
      return SeparateBilling.FreightIncorporated; // Incorporate on Price → FREIGHT INCORPORATED
    default:
      return SeparateBilling.No; // N/A / blank → NO
  }
}

/** A non-NO billing value needs the coordinator to confirm the freight-line choice. */
export function freightBillingNeedsPrompt(separateBilling: string): boolean {
  return (
    separateBilling === SeparateBilling.Yes ||
    separateBilling === SeparateBilling.FreightIncorporated
  );
}

/** Whether the FREIGHT line is posted, from the separate-billing value + the coordinator's
 *  per-order choice. Mirrors the backend ShouldIncludeFreightLine: NO always includes; for
 *  the two non-NO cases the explicit choice wins, else the billing-type default applies
 *  (Separate Billing → include, Freight Incorporated → omit). */
export function resolveIncludeFreightLine(
  separateBilling: string,
  includeFreightLine: boolean | null,
): boolean {
  if (!freightBillingNeedsPrompt(separateBilling)) return true;
  if (includeFreightLine != null) return includeFreightLine;
  return separateBilling === SeparateBilling.Yes;
}
